import type { DateTime } from "luxon";
import type { BaseNurseShift, BaseWage, Nurse, NurseInShift, ShiftPerson } from "./models";
import { WageType } from "./models";
import { isAgencyNurse } from "./warningDialogs/utils";

/**
 * Resolve whether a worked shift is excluded from PPD/HPPD.
 *
 * With job management on, the per-shift flag (resolved by the backend against the
 * shift's job + dept override) is authoritative. Otherwise we fall back to the
 * nurse-level flag. Each caller resolves its own shift/nurse values however it has
 * them (direct ref, options bag, or a lookup) and passes the booleans in.
 */
export const isShiftExcludedFromPpd = (
	jobManagementEnabled: boolean,
	shiftExcludeFromPpd: boolean | undefined,
	nurseExcludeFromPpd: boolean | undefined,
): boolean =>
	// DEFAULT: an absent flag means "included" — legacy nurses/shifts predate PPD exclusion
	// and keep their historical behavior of counting toward PPD.
	jobManagementEnabled ? !!shiftExcludeFromPpd : !!nurseExcludeFromPpd;

/**
 * Find the BaseWage entry that applies at a given dateTime.
 * Mirrors backend get_wage_by_date in calcaulte_cost_logic.py.
 */
export function getBaseWageByTime(nurse: Nurse, dateTime: DateTime): BaseWage | undefined {
	if (!nurse.baseWages || nurse.baseWages.length === 0) return undefined;

	const utcMillis = dateTime.toUTC().toMillis();

	return nurse.baseWages.find((bw) => {
		if (!bw.wage || bw.wage <= 0) return false;
		const startMillis = bw.startTime.toUTC().toMillis();
		if (startMillis > utcMillis) return false;
		if (bw.endTime) {
			const endMillis = bw.endTime.toUTC().toMillis();
			if (endMillis <= utcMillis) return false;
		}
		return true;
	});
}

/**
 * Whether a shift's nurse is salaried (annually paid) on the shift's date. Salaried staff stay out of shift delta,
 * since their extra hours are part of their salary, and aren't expected to punch.
 */
export type IsShiftSalaried = (shift: Pick<BaseNurseShift, "nurseId" | "startTime" | "salaried">) => boolean;

/**
 * For the old list's shifts, which don't say: the nurse's base wage for the shift date (not the deprecated
 * `nurse.wageType`). No base wage for the date → not salaried.
 */
export const salariedFromWageHistory =
	(getNurseById: (id: number) => Nurse | undefined): IsShiftSalaried =>
	(shift) => {
		const nurse = getNurseById(shift.nurseId);
		return !!nurse && getBaseWageByTime(nurse, shift.startTime)?.wageType === WageType.Annually;
	};

/**
 * For the schedule cache's shifts, which carry it.
 * DEFAULT: null (the user can't see wages, and the roster then sends them no wage history either) and absent (a shift
 * added here since its week loaded) read as hourly.
 */
export const salariedFromSchedule: IsShiftSalaried = (shift) => shift.salaried === true;

/** What the punch rule reads about a shift's nurse: a roster nurse, or a schedule person while the roster loads. */
export type PunchPerson = Pick<ShiftPerson, "employmentType" | "lastPunchTime" | "punchHistoryLoaded">;

/**
 * No punch for this long means we stop expecting punches. Product picked 3 months.
 * Not `nurse_inactive_after_days` (90) — that means "has this person left?", not "do they clock in?".
 */
export const NON_PUNCHING_STAFF_MONTHS = 3;

/**
 * Should this nurse clock in for `shift`? No if either is true: they are on an annual salary that day, or they have
 * no punch here within `NON_PUNCHING_STAFF_MONTHS` (never punched counts).
 *
 * Two known trade-offs: substitutions don't reach PPD/HPPD (that reads raw `nursesActual`), and since
 * we store only the newest punch, a nurse's first punch makes older days expect punches again.
 */
export function isNursePunchExpected(
	person: PunchPerson | undefined,
	shift: Pick<BaseNurseShift, "nurseId" | "startTime" | "salaried">,
	isSalaried: IsShiftSalaried,
): boolean {
	// No nurse means no reason to hide a flag.
	if (!person) return true;
	// Agency staff are skipped: most never punched here, and counting their schedule as worked would
	// inflate billed agency hours. `computePunchSummary` already has an Agency bucket for their gaps.
	if (isAgencyNurse(person.employmentType)) return true;
	if (isSalaried(shift)) return false;
	// Without punch history we can't tell "never punches" from "we didn't look", so flag it.
	if (!person.punchHistoryLoaded) return true;
	if (!person.lastPunchTime) return false;
	return person.lastPunchTime >= shift.startTime.minus({ months: NON_PUNCHING_STAFF_MONTHS });
}

/**
 * Whether a shift's wage breakdown is available: not for annually-paid or PPD-excluded nurses. A nurse with no base
 * wage gets one too, as the backend estimates their wage from the department's role/job average (DAT-189).
 *
 * When job management is enabled, PPD exclusion is resolved at the shift/job
 * level (`shift.excludeFromPpd`) rather than the nurse level (`person.excludeFromPpd`).
 */
export function isWageBreakdownAvailable(
	person: Pick<ShiftPerson, "excludeFromPpd"> | undefined,
	shift: Pick<BaseNurseShift, "nurseId" | "startTime" | "salaried" | "excludeFromPpd">,
	isSalaried: IsShiftSalaried,
	jobManagementEnabled: boolean,
): boolean {
	if (!person) return false;
	if (isShiftExcludedFromPpd(jobManagementEnabled, shift.excludeFromPpd, person.excludeFromPpd)) return false;
	return !isSalaried(shift);
}

/**
 * DAT-906 · Whether a shift's row can open its wage breakdown panel: the conditions the day schedule's
 * table bodies put on the row's arrow and on rendering the panel, apart from the Show-wage reveal.
 */
export const canOpenShiftWageBreakdown = (
	shift: NurseInShift,
	person: Pick<ShiftPerson, "excludeFromPpd"> | undefined,
	isSalaried: IsShiftSalaried,
	jobManagementEnabled: boolean,
): boolean => !!shift.startTime && !!shift.endTime && !!shift.cost && isWageBreakdownAvailable(person, shift, isSalaried, jobManagementEnabled);
